import { tokenizeInteractiveText } from './textPatterns';

const MARKDOWN_FORMATTING_PATTERN = /(?:^|\n)\s{0,3}(?:#{1,6}\s|>\s|[-*+]\s|\d+\.\s)|(?:^|\n)\s*[-*+]\s+\[(?: |x|X)\]\s+|\[[^\]]+\]\([^)]+\)|```|`[^`\n]+`|\+\+[^+\n].*?\+\+|==[^=\n].*?==|(?:^|[^\w])(?:\*\*|__)[^*_]+(?:\*\*|__)|(?:^|[^\w])(?:\*|_)[^*_\n]+(?:\*|_)/m;
export const hasMarkdownFormatting = (text = '') => typeof text === 'string' && MARKDOWN_FORMATTING_PATTERN.test(text);
const INLINE_MARKDOWN_TOKEN_REGEX = /(`[^`\n]+`|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/g;
const MARKDOWN_FENCE_REGEX = /^ {0,3}(`{3,}|~{3,})/;
const getMarkdownFenceState = line => {
    const match = String(line || '').match(MARKDOWN_FENCE_REGEX);
    if (!match) {
        return null;
    }

    return {
        char: match[1][0],
        length: match[1].length
    };
};

const isMarkdownFenceEnd = (line, fenceState) => {
    if (!fenceState) {
        return false;
    }

    const trimmed = String(line || '').trimStart();
    if (!trimmed || trimmed[0] !== fenceState.char) {
        return false;
    }

    let markerLength = 0;
    while (markerLength < trimmed.length && trimmed[markerLength] === fenceState.char) {
        markerLength += 1;
    }

    return markerLength >= fenceState.length && trimmed.slice(markerLength).trim() === '';
};

const transformMarkdownOutsideFences = (lines, transformer) => {
    const output = [];
    const outsideFenceChunk = [];
    let fenceState = null;

    const flushChunk = () => {
        if (outsideFenceChunk.length === 0) {
            return;
        }

        output.push(...transformer(outsideFenceChunk));
        outsideFenceChunk.length = 0;
    };

    for (const line of lines) {
        if (fenceState) {
            output.push(line);
            if (isMarkdownFenceEnd(line, fenceState)) {
                fenceState = null;
            }
            continue;
        }

        const nextFenceState = getMarkdownFenceState(line);
        if (nextFenceState) {
            flushChunk();
            output.push(line);
            fenceState = nextFenceState;
            continue;
        }

        outsideFenceChunk.push(line);
    }

    flushChunk();
    return output;
};

export const getValidMarkdownAnchorProps = props =>
    Object.keys(props || {}).reduce((validProps, key) => {
        if (key.includes(':') || key === 'href') {
            return validProps;
        }

        const value = props[key];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            validProps[key] = value;
        }

        return validProps;
    }, {});

export const normalizeMarkdownLinkHref = value => {
    const rawValue = String(value || '').trim();
    if (!rawValue) {
        return '';
    }

    if (rawValue.startsWith('@http')) {
        return rawValue.slice(1);
    }

    if (/^(?:https?:\/\/|mention:|hashtag:|stock:)/i.test(rawValue)) {
        return rawValue;
    }

    return `https://${rawValue}`;
};

const linkifyPlainTitleSegment = (segment = '') => {
    const tokens = tokenizeInteractiveText(segment);
    if (!tokens) {
        return '';
    }

    return tokens
        .map(token => {
            switch (token.type) {
                case 'markdownLink':
                    return `[${token.text}](${token.url})`;
                case 'url':
                    return `[${token.text}](${normalizeMarkdownLinkHref(token.text)})`;
                case 'mention':
                    return `[${token.text}](mention:${encodeURIComponent(token.text.slice(1))})`;
                case 'hashtag':
                    return `[${token.text}](hashtag:${encodeURIComponent(token.text.slice(1))})`;
                case 'stockTicker':
                    return `[${token.text}](stock:${encodeURIComponent(token.text)})`;
                case 'email':
                    return `[${token.text}](copy-email:${encodeURIComponent(token.text)})`;
                case 'phone':
                    return `[${token.text}](copy-phone:${encodeURIComponent(token.text)})`;
                case 'protocolUrl':
                case 'text':
                default:
                    return token.text;
            }
        })
        .join('');
};

export const linkifyMarkdownTitle = (markdown = '') => {
    if (typeof markdown !== 'string' || markdown.length === 0) {
        return '';
    }

    const processedLines = transformMarkdownOutsideFences(markdown.split('\n'), lines =>
        lines.map(line => {
            let output = '';
            let lastIndex = 0;
            let match;
            const tokenRegex = new RegExp(INLINE_MARKDOWN_TOKEN_REGEX.source, 'g');

            while ((match = tokenRegex.exec(line)) !== null) {
                output += linkifyPlainTitleSegment(line.slice(lastIndex, match.index));
                output += match[0];
                lastIndex = match.index + match[0].length;
            }

            output += linkifyPlainTitleSegment(line.slice(lastIndex));
            return output;
        })
    );

    return processedLines.join('\n');
};
